import { Trans, useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { Textarea } from 'designSystem/components/Textarea'
import type { DocChatQueuedMessage } from 'features/knowledgeDocumentViewer/types'
import { Pencil, X } from 'lucide-react'
import React, { useState } from 'react'

import { Content } from './docChatQueuedMessageRow.styles'

export function DocChatQueuedMessageRow({
  message,
  position,
  onEdit,
  onSave,
  onCancelEdit,
  onRemove,
}: {
  message: DocChatQueuedMessage
  position: number
  onEdit: (id: string) => void
  onSave: (id: string, query: string) => void
  onCancelEdit: (id: string) => void
  onRemove: (id: string) => void
}) {
  const { t } = useLingui()
  const [draft, setDraft] = useState(message.query)
  const handleEdit = () => {
    setDraft(message.query)
    onEdit(message.id)
  }
  const handleSave = () => onSave(message.id, draft.trim())
  const handleCancelEdit = () => onCancelEdit(message.id)
  const handleRemove = () => onRemove(message.id)
  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setDraft(event.target.value)

  return (
    <Box
      asChild
      display='flex'
      align='start'
      gap='sm'
      padding='sm'
      border='default'
      radius='xl'
      surface='muted'>
      <li>
        <Text variant='caption' color='muted'>
          {position}.
        </Text>
        <Box asChild flex='one' minWidth='none'>
          <Content>
            {message.isEditing ? (
              <Textarea
                aria-label={t`Edit queued message ${position}`}
                value={draft}
                onChange={handleChange}
                autoFocus
              />
            ) : (
              <Text variant='body'>{message.query}</Text>
            )}
            {message.attachments?.map((attachment) => (
              <Text key={attachment.id} as='p' variant='caption' color='muted'>
                {attachment.name}
              </Text>
            ))}
          </Content>
        </Box>
        <Box display='flex' gap='xs' shrink={false}>
          {message.isEditing ? (
            <>
              <Button
                size='sm'
                disabled={!draft.trim() && !message.attachments?.length}
                onClick={handleSave}>
                <Trans>Save</Trans>
              </Button>
              <Button variant='ghostMuted' size='sm' onClick={handleCancelEdit}>
                <Trans>Cancel</Trans>
              </Button>
            </>
          ) : (
            <Button
              variant='ghostMuted'
              size='icon-xs'
              aria-label={t`Edit queued message ${position}`}
              onClick={handleEdit}>
              <Pencil />
            </Button>
          )}
          <Button
            variant='ghostMuted'
            size='icon-xs'
            aria-label={t`Remove queued message ${position}`}
            onClick={handleRemove}>
            <X />
          </Button>
        </Box>
      </li>
    </Box>
  )
}
